import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'

import { defaultChartMenuItems } from '@aytm/app/insights/dashboard/lib/charts'
import { defaultColors, defaultPatterns } from '@aytm/app/insights/dashboard/lib/colors'
import { truncateStringToLength } from '@aytm/app/insights/dashboard/lib/strings'
import { HighchartsReact } from '@aytm/app/insights/dashboard/components/ui/Highcharts'

import { useAttributes, useValue, useFullscreenStatus } from '@aytm/app/insights/dashboard/components/hooks'

import { showCollapsedSections } from '../config'
import { saveFullscreenSeriesState, restoreFullscreenSeriesState } from '../fullscreenSeriesState'
import { stateSelector, shouldUpdate } from './selectors'

import './AttributeChart.scss'
import { composeSeriesForChart } from '@aytm/app/insights/dashboard/components/ui/SplitChart/AttributeChart/composeSeriesForChart'

const CLASS = 'insights-portal-attribute-chart'

export const bandValue = confidenceLevel => {
  const value = confidenceLevel * 100
  return `Answers with statistically non-significance difference at ${value}% Confidence Level`
}

export const bandInterval = (low, high) => {
  const [rLow, rHigh] = [Math.round(low * 10) / 10, Math.round(high * 10) / 10]
  return `(+ ${rLow}% - ${rHigh}%) confidence interval`
}

const PRINT_MAX_WIDTH = 1024

const exportingBase = {
  printMaxWidth: PRINT_MAX_WIDTH,
  chartOptions: {
    chart: {
      marginRight: undefined
    },
    legend: {
      itemDistance: 32,
      symbolHeight: 12,
      symbolWidth: 12,
      itemStyle: {
        fontSize: 12
      }
    }
  }
}

const normalViewExporting = {
  buttons: {
    contextButton: {
      menuItems: defaultChartMenuItems
    }
  }
}

const fullscreenViewExporting = {
  buttons: {
    contextButton: {
      menuItems: [
        {
          textKey: 'exitFullscreen',
          onclick: function () {
            this.fullscreen.toggle()
          }
        }
      ].concat([...defaultChartMenuItems.filter(v => v !== 'viewFullscreen' && v !== 'printChart')])
    }
  }
}

/**
 *
 * @param {'all'|Array<'data'|'layout'|'exporting'>} updateType
 * @param {*} param1
 * @returns
 */
function getConfig(updateType, { width, patterns, isSharedView, metrics, series, attribute, isFullScreen }) {
  const colWidth = width && series && series.length ? width / series.length : 0
  const marginRight = colWidth ? -2 * colWidth : 0

  const exporting = {
    ...exportingBase,
    enabled: !isSharedView,
    ...normalViewExporting
  }

  const layout = {
    defs: {
      patterns
    },
    chart: {
      reflow: true,
      marginTop: 140,
      marginRight,
      events: {
        fullscreenOpen: function () {
          saveFullscreenSeriesState(this)
          this.update({
            exporting: {
              ...fullscreenViewExporting
            }
          })
        },
        fullscreenClose: function () {
          restoreFullscreenSeriesState(this)
          this.update({
            exporting: {
              ...normalViewExporting
            }
          })
        }
        // beforePrint: function () {},
        // afterPrint: function () {}
      }
    },
    title: {
      text: isFullScreen ? truncateStringToLength(attribute, 50) : null,
      style: {
        fontSize: 22,
        color: '#444'
      }
    },
    credits: {
      enabled: false
    },
    xAxis: {
      type: 'category',
      labels: {
        enabled: false
      },
      crosshair: true,
      minPadding: 0,
      maxPadding: 0
    },
    yAxis: {
      min: 0,
      title: {
        text: metrics && metrics.name ? metrics.name : 'Top2Box (%)'
      }
    },
    tooltip: {
      headerFormat: null,
      borderColor: '#666666',
      pointFormat: `
    <span style="color:#666666;padding:0;">{series.name}</span> <br />
    <span style="color:#29abe2;font-weight:bold;padding:0;">{point.y:.2f}</span>
    <span style="color:#666666;padding:0;">{point.metadata.suffix} (+{point.metadata.high:.1f}{point.metadata.suffix} -{point.metadata.low:.1f}{point.metadata.suffix})</span>
  `
    },
    legend: {
      floating: true,
      enabled: true,
      verticalAlign: 'top',
      itemMarginTop: 6,
      itemDistance: 24,
      itemMarginBottom: 2,
      align: 'left',
      symbolHeight: 15,
      symbolWidth: 15,
      symbolRadius: 8,
      itemStyle: {
        fontSize: 15
      },
      maxHeight: 75,
      y: 26,
      alignColumns: false
    },
    colors: defaultColors,
    plotOptions: {
      series: {
        animation: false,
        states: {
          inactive: {
            opacity: 1
          }
        }
      },
      column: {
        pointPadding: 0.1,
        groupPadding: 0.01,
        borderWidth: 0
      }
    }
  }

  const isData = 'all' === updateType || updateType.includes('data')
  const isLayout = 'all' === updateType || updateType.includes('layout')
  const isExporting = 'all' === updateType || updateType.includes('exporting')

  return Object.assign(isLayout ? layout : {}, isExporting ? { exporting } : {}, isData ? { series } : {})
}

const AttributeChart = ({ items = [], onClose: propsOnClose, className: propsClassName }) => {
  const [width, setWidth] = useState(null)

  const hasColumns = items.length > 0

  const containerRef = useRef()

  const { isSharedView, confidenceLevel } = usePool(InsightsDashboardPool, stateSelector, shouldUpdate)

  const { sortByAttribute, sortByMethod, chartAttribute, getMetrics, getCaptions } = useAttributes()

  const { getColumnData, getCellData } = useValue()

  // chartAttribute holds the last non-meta sort attr; falls back to sortByAttribute when unset
  const effectiveAttr = chartAttribute ?? sortByAttribute
  const isDecoupled = effectiveAttr !== sortByAttribute

  const visibleCaptions = useMemo(
    () =>
      getCaptions({
        showSections: false,
        showMetrics: false,
        showCollapsed: showCollapsedSections,
        showMetadata: false
      }),
    [getCaptions]
  )

  const isAttrVisible = useMemo(
    () => Boolean(effectiveAttr && visibleCaptions.some(c => c.attribute === effectiveAttr)),
    [effectiveAttr, visibleCaptions]
  )

  const metrics = useMemo(
    () => getMetrics(effectiveAttr, null, isDecoupled ? null : sortByMethod),
    [effectiveAttr, getMetrics, sortByMethod, isDecoupled]
  )

  const defaultMetrics = useMemo(() => getMetrics(effectiveAttr), [effectiveAttr, getMetrics])

  const selected = useMemo(
    () => effectiveAttr && (isDecoupled || !metrics?.id || metrics?.id === sortByMethod),
    [metrics?.id, effectiveAttr, sortByMethod, isDecoupled]
  )

  const series = useMemo(
    () =>
      selected
        ? composeSeriesForChart({
            items,
            sortByAttribute: effectiveAttr,
            metrics,
            defaultMetrics,
            confidenceLevel,
            getColumnData,
            getCellData
          })
        : [],
    [selected, items, effectiveAttr, metrics, defaultMetrics, confidenceLevel, getColumnData, getCellData]
  )

  const onClose = () => {
    propsOnClose?.()
  }

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      if (entry?.target) {
        const rect = entry.target.getBoundingClientRect()
        setWidth(rect ? rect.width : null)
      }
    })
    const node = containerRef.current?.container.current
    if (node) {
      const rect = node.getBoundingClientRect()
      setWidth(rect ? rect.width : null)
      observer.observe(node)
    }
    return () => {
      if (node) {
        observer.unobserve(node)
      }
    }
  }, [])

  useEffect(() => {
    const node = containerRef.current?.container.current
    if (items.length && node) {
      setTimeout(() => {
        const rect = node.getBoundingClientRect()
        setWidth(rect ? rect.width + Math.random() : null)
      }, 10)
    } else {
      setWidth(null)
    }
  }, [items.length])

  const className = propsClassName || CLASS

  const isFullScreen = useFullscreenStatus()

  const options = useMemo(
    () =>
      getConfig('all', {
        patterns: defaultPatterns,
        series,
        attribute: effectiveAttr,
        width,
        metrics,
        isSharedView,
        isFullScreen
      }),
    [effectiveAttr, isFullScreen, isSharedView, metrics, series, width]
  )

  useEffect(() => {
    if (containerRef.current?.chart) {
      containerRef.current.chart.update(
        getConfig(['data', 'layout'], {
          patterns: defaultPatterns,
          series,
          attribute: effectiveAttr,
          width,
          metrics,
          isSharedView,
          isFullScreen
        }),
        true,
        true,
        false
      )
    }
  }, [effectiveAttr, isSharedView, metrics, series, width, isFullScreen])

  return hasColumns && isAttrVisible ? (
    <div className={className}>
      <HighchartsReact
        key={compoundKeyFor(items)}
        ref={containerRef}
        options={options}
        className={`${className}-graph`}
        allowChartUpdate={false}
      />
      <div className={`${className}-control`}>
        <i className={`pi pi-times ${className}-control-close`} onClick={onClose}></i>
      </div>
    </div>
  ) : (
    <div className={className}> </div>
  )
}

export function compoundKeyFor(items) {
  return items.map(v => v.uid).join('|')
}

AttributeChart.propTypes = {
  wait: PropTypes.bool,
  className: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func
}

const propsAreEqual = (prev, cur) =>
  !cur.wait && prev.items === cur.items && prev.className === cur.className && prev.onClose === cur.onClose

export default React.memo(AttributeChart, propsAreEqual)
